import React, { useState, useEffect } from 'react';
import { ArrowLeft, Moon, Sun, Upload } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = () => {
  const [darkMode, setDarkMode] = useState(false);
  const [autoMute, setAutoMute] = useState(false);
  const [saveHistory, setSaveHistory] = useState(true);
  const [companyName, setCompanyName] = useState('AI Voice');
  const [logoUrl, setLogoUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadBranding();
  }, []);

  const loadBranding = async () => {
    try {
      const response = await axios.get(`${API}/branding`);
      setCompanyName(response.data.company_name || 'AI Voice');
      setLogoUrl(response.data.logo_url || '');
    } catch (error) {
      console.error('Error loading branding:', error);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/branding/upload-logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setLogoUrl(response.data.logo_url);
      toast.success('Logo uploaded successfully!');
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const saveBranding = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/branding`, {
        company_name: companyName || null,
        logo_url: logoUrl
      });
      toast.success('Branding saved successfully!');
    } catch (error) {
      console.error('Error saving branding:', error);
      toast.error('Failed to save branding');
    } finally {
      setSaving(false);
    }
  };

  const toggleDarkMode = (checked) => {
    setDarkMode(checked);
    document.documentElement.classList.toggle('dark', checked);
    toast.success(checked ? 'Dark mode enabled' : 'Light mode enabled');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card px-6 py-4 flex items-center gap-4">
        <Link to="/">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <h1 className="text-2xl font-bold">Settings</h1>
      </header>

      {/* Content */}
      <div className="max-w-2xl mx-auto p-6 space-y-6">
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Company Branding</h2>
          <div className="space-y-4">
            <div>
              <Label htmlFor="company-name" className="text-base font-medium">Company Name (Optional)</Label>
              <Input
                id="company-name"
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Enter company name (optional)"
                className="mt-2"
              />
            </div>
            
            <div>
              <Label className="text-base font-medium">Company Logo</Label>
              <p className="text-sm text-muted-foreground mb-3">Upload your company logo (recommended: square image, PNG or JPG)</p>
              
              {logoUrl && (
                <div className="mb-3 flex items-center gap-3 p-3 bg-secondary rounded-lg">
                  <img src={logoUrl} alt="Company logo preview" className="h-12 w-12 object-contain rounded" />
                  <span className="text-sm text-muted-foreground">Current logo</span>
                </div>
              )}
              
              <div className="flex items-center gap-4">
                <Button
                  onClick={() => document.getElementById('logo-upload').click()}
                  disabled={uploading}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Upload className="h-4 w-4" />
                  {uploading ? 'Uploading...' : 'Upload Logo'}
                </Button>
                <input
                  id="logo-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
              </div>
            </div>

            <Button 
              onClick={saveBranding} 
              disabled={saving}
              className="w-full"
            >
              {saving ? 'Saving...' : 'Save Branding'}
            </Button>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Appearance</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {darkMode ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
                <div>
                  <Label htmlFor="dark-mode" className="text-base font-medium">Dark Mode</Label>
                  <p className="text-sm text-muted-foreground">Toggle dark theme</p>
                </div>
              </div>
              <Switch
                id="dark-mode"
                checked={darkMode}
                onCheckedChange={toggleDarkMode}
              />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Voice Settings</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="auto-mute" className="text-base font-medium">Auto Mute</Label>
                <p className="text-sm text-muted-foreground">Automatically mute voice output in quiet mode</p>
              </div>
              <Switch
                id="auto-mute"
                checked={autoMute}
                onCheckedChange={setAutoMute}
              />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Privacy</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="save-history" className="text-base font-medium">Save Conversation History</Label>
                <p className="text-sm text-muted-foreground">Store conversations for future reference</p>
              </div>
              <Switch
                id="save-history"
                checked={saveHistory}
                onCheckedChange={setSaveHistory}
              />
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-secondary">
          <h2 className="text-xl font-semibold mb-2">OpenAI API Configuration</h2>
          <p className="text-sm text-muted-foreground mb-4">
            To enable voice features, add your OpenAI API key in the backend <code className="bg-background px-2 py-1 rounded">.env</code> file:
          </p>
          <div className="bg-background p-4 rounded font-mono text-sm">
            OPENAI_API_KEY=your_api_key_here
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Get your API key from the <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="underline hover:text-primary">OpenAI Platform Dashboard</a>
          </p>
        </Card>
      </div>
    </div>
  );
};

export default Settings;